/**
 * Permit Wall logic + palette (DISPLAY-only office TV).
 *
 * Pure, framework-free helpers so the wall's data derivation is unit-
 * testable without a DOM: it turns the dashboard's transaction list into
 * the two zones the wall renders (top cards + ticker), resolves the
 * status-pill and event colors, and computes the working-day window that
 * bounds the ticker.
 *
 * Design rules enforced here (from the build brief):
 *   • The status pill reads `Transaction.requestStatus` (the top-block
 *     حالة الطلب), never the per-action `Action.status`. Five known
 *     labels map to tuned colors; anything else (including the sentence-
 *     like per-action values) lands on a DISTINCT neutral, never on a
 *     real status's style.
 *   • The arrival event (موافقة / عدم موافقة / تكليف) is detected with
 *     the shared exact standalone-token rule (`detectWallEvent`) and only
 *     ever against the latest action's `finalAction` - the same field the
 *     dashboard classifies - so an in-progress row with an empty
 *     finalAction never flashes.
 *   • "Most recently updated" is `lastChangedAt`, which the scraper
 *     stamps only on a real action-table diff (never the optimistic
 *     enqueue claim - that's `lastCheckedAt`). The ticker window derives
 *     from `lastChangedAt` + the shared workdays weekend definition, not
 *     a hardcoded calendar-day count.
 */

import { isWeekend, DEFAULT_WEEKEND_DAYS, type Weekday } from "../../../../packages/workdays/index.js";
import { detectWallEvent, type WallEvent } from "../../../../packages/keywords/index.js";
import type { Action, Transaction } from "@/api/types";

export type WallTheme = "light" | "dark";

export interface PillColor {
  bg: string;
  fg: string;
  dot: string;
}

export interface EventColor {
  /** Update-text color when this event is present. */
  text: string;
  /** Flash ring color on the newest-arrival card. */
  line: string;
  /** Flash glow color (already includes its own alpha). */
  glow: string;
}

/**
 * Per-theme surface tokens, applied as CSS custom properties on the wall
 * root. Dark mirrors the prototype; light is rebuilt for legibility on a
 * bright background rather than copied from the prototype's stub.
 */
export const WALL_SURFACE: Record<WallTheme, Record<string, string>> = {
  dark: {
    "--page-bg": "radial-gradient(130% 100% at 82% -12%, #161e2c 0%, #0d1118 48%, #0a0c11 100%)",
    "--text": "#eef1f6",
    "--text-dim": "#7d8798",
    "--num": "#8a94a6",
    "--card-bg": "linear-gradient(180deg,#171d27 0%,#11151d 100%)",
    "--card-border": "rgba(255,255,255,0.06)",
    "--card-shadow": "0 1.4vh 3.4vh rgba(0,0,0,0.4)",
    "--ticker-bg": "linear-gradient(180deg,#0e131c 0%,#0a0c11 100%)",
    "--divider": "rgba(255,255,255,0.08)",
    "--ticker-item-border": "rgba(255,255,255,0.07)",
    "--ticker-fade": "linear-gradient(270deg,transparent,#0a0c11 52%)",
    "--overlay-bg": "radial-gradient(120% 90% at 50% 42%, rgba(18,24,34,0.88), rgba(7,9,13,0.97))",
    "--toggle-track": "rgba(255,255,255,0.07)",
    "--toggle-border": "rgba(255,255,255,0.12)",
    "--toggle-knob": "#e8ecf2",
    "--toggle-icon": "rgba(255,255,255,0.4)",
  },
  light: {
    // Warm off-white canvas so the dark Cairo text and saturated pills
    // keep contrast without the glare of pure #fff on a wall TV.
    "--page-bg": "radial-gradient(130% 100% at 82% -12%, #ffffff 0%, #f3f4ef 48%, #e7e9e1 100%)",
    "--text": "#1b2330",
    "--text-dim": "#586073",
    "--num": "#5c6470",
    "--card-bg": "linear-gradient(180deg,#ffffff 0%,#f5f6f1 100%)",
    "--card-border": "rgba(20,28,40,0.12)",
    "--card-shadow": "0 1.2vh 3vh rgba(40,50,70,0.12)",
    "--ticker-bg": "linear-gradient(180deg,#eef0e9 0%,#e4e6de 100%)",
    "--divider": "rgba(20,28,40,0.12)",
    "--ticker-item-border": "rgba(20,28,40,0.09)",
    "--ticker-fade": "linear-gradient(270deg,transparent,#e4e6de 52%)",
    "--overlay-bg": "radial-gradient(120% 90% at 50% 42%, rgba(249,250,246,0.93), rgba(228,230,222,0.97))",
    "--toggle-track": "rgba(20,28,40,0.08)",
    "--toggle-border": "rgba(20,28,40,0.16)",
    "--toggle-knob": "#2b3340",
    "--toggle-icon": "rgba(20,28,40,0.45)",
  },
};

/** Event flash/text colors - pastel on dark, deep + high-contrast on light. */
export const WALL_EVENT_COLORS: Record<WallTheme, Record<WallEvent, EventColor>> = {
  dark: {
    APPROVAL: { text: "#a6e9c6", line: "#8adcb1", glow: "rgba(150,230,190,0.40)" },
    REJECTION: { text: "#ffa89c", line: "#ff9f93", glow: "rgba(255,165,155,0.40)" },
    ASSIGNMENT: { text: "#ffd29c", line: "#ffc78e", glow: "rgba(255,205,150,0.40)" },
  },
  light: {
    APPROVAL: { text: "#0f7a45", line: "#15a357", glow: "rgba(21,163,87,0.30)" },
    REJECTION: { text: "#bd2f22", line: "#d93b2b", glow: "rgba(217,59,43,0.26)" },
    ASSIGNMENT: { text: "#8a5410", line: "#cf870f", glow: "rgba(207,135,15,0.26)" },
  },
};

/** Status pills - same hues, tuned per background for distance legibility.
 *  قيد الدراسة + منتهي بالوقت are intentionally muted; the neutral
 *  (`__unknown__`) is a distinct violet so an unrecognized status can
 *  never be mistaken for one of the five real ones. */
const WALL_STATUS_COLORS: Record<WallTheme, Record<string, PillColor>> = {
  dark: {
    مكتمل: { bg: "rgba(130,225,180,0.16)", fg: "#a8e9c8", dot: "#86dcaf" },
    مرفوض: { bg: "rgba(255,160,150,0.15)", fg: "#ffab9f", dot: "#ff9b8e" },
    مكلف: { bg: "rgba(255,205,155,0.17)", fg: "#ffcf9e", dot: "#ffc086" },
    "قيد الدراسة": { bg: "rgba(150,185,228,0.16)", fg: "#c2d8f1", dot: "#97b9e3" },
    "منتهي بالوقت": { bg: "rgba(185,172,152,0.15)", fg: "#d2c4ab", dot: "#ad9e82" },
    __unknown__: { bg: "rgba(190,165,228,0.17)", fg: "#d6c4ef", dot: "#b79cdf" },
  },
  light: {
    مكتمل: { bg: "rgba(21,163,87,0.13)", fg: "#0f7a45", dot: "#15a357" },
    مرفوض: { bg: "rgba(217,59,43,0.12)", fg: "#bd2f22", dot: "#d93b2b" },
    مكلف: { bg: "rgba(207,135,15,0.15)", fg: "#8a5410", dot: "#cf870f" },
    "قيد الدراسة": { bg: "rgba(46,108,184,0.12)", fg: "#23529b", dot: "#3a78d6" },
    "منتهي بالوقت": { bg: "rgba(120,104,78,0.14)", fg: "#695837", dot: "#897149" },
    __unknown__: { bg: "rgba(124,86,176,0.13)", fg: "#583a8e", dot: "#8a5cc0" },
  },
};

/**
 * Resolves the pill color for a `requestStatus` value. Exact string
 * equality on the five known labels; any other value (null, the
 * sentence-like per-action status, an unseen portal label) returns the
 * distinct neutral.
 */
export function pillFor(theme: WallTheme, requestStatus: string | null | undefined): PillColor {
  const map = WALL_STATUS_COLORS[theme];
  const key = (requestStatus ?? "").trim();
  return map[key] ?? map["__unknown__"]!;
}

/** A transaction with its latest actions attached, as the list endpoint
 *  returns it under `?include=actions`. */
export type WallInput = Transaction & { actions?: Action[] };

/** One rendered line, shared by the top cards and the ticker. */
export interface WallRow {
  id: string;
  /** Largest text: owner name, falling back to the request number. */
  name: string;
  /** `<requestNumber>/<year>`, rendered LTR with Western digits. */
  number: string;
  /** Latest update text: the newest action's finalAction, or its
   *  per-action status when finalAction is blank (in-progress rows). */
  update: string;
  /** Raw requestStatus for the pill (top-block حالة الطلب). */
  requestStatus: string | null;
  /** Arrival event, detected ONLY from the latest finalAction. */
  event: WallEvent | null;
  /** ISO timestamp the row last actually changed. */
  lastChangedAt: string | null;
}

export interface DeriveOptions {
  /** Fixed number of top-zone cards (the brief pins this to 5). */
  topCount: number;
  /** Working-day window length for the ticker (default 3). */
  windowDays: number;
  /** Weekend definition; defaults to the shared Jordan calendar. */
  weekend?: readonly Weekday[];
  /** Injected for deterministic tests; defaults to now. */
  now?: Date;
}

export interface WallData {
  topRows: WallRow[];
  tickerRows: WallRow[];
}

function toRow(tx: WallInput): WallRow {
  const latest = tx.actions?.[0];
  const finalText = latest?.finalAction?.trim() ?? "";
  // finalAction is empty on in-progress rows; fall back to the per-action
  // status purely for DISPLAY. Event detection below stays on finalAction.
  const update = finalText.length > 0 ? finalText : (latest?.status?.trim() ?? "");
  return {
    id: tx.id,
    name: tx.ownerName?.trim() || tx.requestNumber,
    number: `${tx.requestNumber}/${tx.year}`,
    update,
    requestStatus: tx.requestStatus,
    event: detectWallEvent(latest?.finalAction ?? null),
    lastChangedAt: tx.lastChangedAt,
  };
}

/** Sort key: most-recently-changed first, nulls last, createdAt as the
 *  tie-break - mirrors the API's own ordering so the wall is stable. */
function changedMs(tx: WallInput): number {
  return tx.lastChangedAt ? new Date(tx.lastChangedAt).getTime() : -Infinity;
}

/**
 * Splits the active transaction list into the two wall zones:
 *   • topRows  - the `topCount` most-recently-changed transactions.
 *   • tickerRows - transactions changed within the working-day window,
 *     EXCLUDING the ones already shown up top (the brief's dedupe rule),
 *     newest first.
 */
export function deriveWall(items: WallInput[], opts: DeriveOptions): WallData {
  const now = opts.now ?? new Date();
  const weekend = opts.weekend ?? DEFAULT_WEEKEND_DAYS;

  const sorted = [...items].sort((a, b) => {
    const d = changedMs(b) - changedMs(a);
    if (d !== 0) return d;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const topItems = sorted.slice(0, Math.max(0, opts.topCount));
  const topIds = new Set(topItems.map((t) => t.id));

  const windowStart = workingWindowStart(now, opts.windowDays, weekend).getTime();
  const tickerItems = sorted.filter((t) => {
    if (topIds.has(t.id)) return false;
    const ms = changedMs(t);
    return ms !== -Infinity && ms >= windowStart;
  });

  return {
    topRows: topItems.map(toRow),
    tickerRows: tickerItems.map(toRow),
  };
}

// ---------------------------------------------------------------------------
// Working-day window
// ---------------------------------------------------------------------------

// Asia/Amman is UTC+3 with no DST (Jordan abolished it in 2012), matching
// the shared @shared/workdays assumption. Minutes keep the arithmetic exact.
const AMMAN_OFFSET_MIN = 3 * 60;

/** UTC instant for 00:00 Asia/Amman of the Amman-calendar date of `d`. */
function ammanMidnight(d: Date): Date {
  const amman = new Date(d.getTime() + AMMAN_OFFSET_MIN * 60_000);
  const y = amman.getUTCFullYear();
  const m = amman.getUTCMonth();
  const day = amman.getUTCDate();
  return new Date(Date.UTC(y, m, day) - AMMAN_OFFSET_MIN * 60_000);
}

/**
 * Returns the UTC instant for the inclusive start of the last `n`
 * working days, counting today (Asia/Amman) as the first when it is a
 * working day. Walks backward day-by-day, skipping weekend days per the
 * shared `isWeekend` definition, and returns the midnight-Amman of the
 * nth counted working day.
 *
 * A change that lands on a weekend is still "recent", so when today is a
 * weekend the window simply starts on the most recent working day and
 * weekend changes pass the `>= start` comparison naturally.
 */
export function workingWindowStart(
  now: Date,
  n: number,
  weekend: readonly Weekday[] = DEFAULT_WEEKEND_DAYS,
): Date {
  const days = Math.max(1, Math.floor(n));
  let cursor = ammanMidnight(now);
  let counted = 0;
  // Bounded: at most `days` working days plus interleaved weekend days.
  for (let guard = 0; guard < days * 4 + 14; guard++) {
    if (!isWeekend(cursor, weekend)) {
      counted += 1;
      if (counted >= days) return cursor;
    }
    // Step to the previous Amman calendar day. Subtracting 12h from an
    // exact midnight-Amman instant lands at the previous day's noon-Amman;
    // re-flooring yields that day's midnight. Safe with no DST.
    cursor = ammanMidnight(new Date(cursor.getTime() - 12 * 3_600_000));
  }
  return cursor;
}
